import PropTypes from 'prop-types';
import {useCallback} from 'react';
import {TextArea} from '@/components/atoms';
import {
  MultiLanguageFieldGroup,
  PageHeader,
  TextInputField
} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {
  MEDIA_PREVIEW_BANNER,
  MEDIA_PREVIEW_CARD
} from '@/components/organisms/MediaPreview/mediaPreviewConfig';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {
  PEPIRIS_BANNER_IMAGE_DIMENSIONS,
  PEPIRIS_GUIDELINE_IMAGE_DIMENSIONS,
  PEPIRIS_THUMBNAIL_IMAGE_DIMENSIONS
} from '@/utils/constants/perfectStoreConstants';
import {
  addGuidelineRow,
  nextGuidelineRows,
  readPepIrisDisplay,
  removeGuidelineRow,
  updateBannerText,
  updateGuidelineText,
  withPepIrisDisplay
} from './pepirisDisplayState';
import PepIrisGuidelinesSection from './PepIrisGuidelinesSection';
import PepIrisImageRow from './PepIrisImageRow';
import {
  pepirisDescriptionUiSchema,
  pepirisTitleUiSchema,
  photoGuidelinesToggleSchema,
  STRING_SCHEMA,
  targetGuidelinesToggleSchema
} from './schemas';

const BANNER_PREVIEW_SCALE = 0.22;
const CARD_PREVIEW_SCALE = 0.45;

const IMAGE_ROWS = [
  {
    key: 'bannerImage',
    label: 'Target Banner Image*',
    dimensions: PEPIRIS_BANNER_IMAGE_DIMENSIONS,
    previewType: MEDIA_PREVIEW_BANNER,
    previewScale: BANNER_PREVIEW_SCALE,
    dataTestId: 'pepiris-banner-image'
  },
  {
    key: 'thumbnailImage',
    label: 'Target Card Thumbnail*',
    dimensions: PEPIRIS_THUMBNAIL_IMAGE_DIMENSIONS,
    previewType: MEDIA_PREVIEW_CARD,
    previewScale: CARD_PREVIEW_SCALE,
    dataTestId: 'pepiris-thumbnail-image'
  },
  {
    key: 'guidelineImage',
    label: 'Target Guideline Image*',
    dimensions: PEPIRIS_GUIDELINE_IMAGE_DIMENSIONS,
    previewType: MEDIA_PREVIEW_BANNER,
    previewScale: BANNER_PREVIEW_SCALE,
    dataTestId: 'pepiris-guideline-image'
  }
];

const PepIrisDisplay = ({formData, setFormData, languages}) => {
  const pepirisDisplay = readPepIrisDisplay(formData);

  const persistPepIrisDisplay = useCallback(
    (partial) => {
      setFormData((prev) => withPepIrisDisplay(prev, partial));
    },
    [setFormData]
  );

  const handleImageChange = useCallback(
    (key, image) => persistPepIrisDisplay({[key]: image}),
    [persistPepIrisDisplay]
  );

  const handleImageDelete = useCallback(
    (key) => persistPepIrisDisplay({[key]: null}),
    [persistPepIrisDisplay]
  );

  const handleBannerTextChange = useCallback(
    (langCode, field, value, hasError) => {
      setFormData((prev) =>
        updateBannerText(prev, langCode, field, value, hasError)
      );
    },
    [setFormData]
  );

  const handleGuidelinesToggle = useCallback(
    (enabledKey, listKey, enabled) => {
      setFormData((prev) => {
        const current = readPepIrisDisplay(prev);
        return withPepIrisDisplay(prev, {
          [enabledKey]: enabled,
          [listKey]: nextGuidelineRows(current[listKey], enabled)
        });
      });
    },
    [setFormData]
  );

  const handleGuidelineChange = useCallback(
    (listKey, index, langCode, value, hasError) => {
      setFormData((prev) =>
        updateGuidelineText(prev, listKey, index, langCode, value, hasError)
      );
    },
    [setFormData]
  );

  const handleAddGuideline = useCallback(
    (listKey) => {
      setFormData((prev) => {
        const current = readPepIrisDisplay(prev);
        return withPepIrisDisplay(prev, {
          [listKey]: addGuidelineRow(current[listKey])
        });
      });
    },
    [setFormData]
  );

  const handleRemoveGuideline = useCallback(
    (listKey, index) => {
      setFormData((prev) => {
        const current = readPepIrisDisplay(prev);
        return withPepIrisDisplay(prev, {
          [listKey]: removeGuidelineRow(current[listKey], index)
        });
      });
    },
    [setFormData]
  );

  return (
    <div data-testid='pepiris-display'>
      <PageContainer>
        <PageHeader
          title='Target Card Images'
          fontSize={FONT_SIZE.LG}
          wrapperClassName='mb-5 mt-0'
        />
        {IMAGE_ROWS.map((row) => (
          <PepIrisImageRow
            key={row.key}
            label={row.label}
            image={pepirisDisplay[row.key]}
            dimensions={row.dimensions}
            previewType={row.previewType}
            previewScale={row.previewScale}
            onChange={(image) => handleImageChange(row.key, image)}
            onDelete={() => handleImageDelete(row.key)}
            dataTestId={row.dataTestId}
          />
        ))}
      </PageContainer>

      <PageContainer>
        <PageHeader
          title='Banner Text'
          fontSize={FONT_SIZE.LG}
          wrapperClassName='mb-5 mt-0'
        />
        <MultiLanguageFieldGroup
          languages={languages}
          dataTestId='pepiris-banner-text-fields'
          renderField={(lang) => {
            const data = pepirisDisplay.bannerText?.[lang.code] || {};
            return (
              <>
                <TextInputField
                  value={data.title || ''}
                  label={pepirisTitleUiSchema.label}
                  schema={STRING_SCHEMA}
                  uiSchema={pepirisTitleUiSchema}
                  updateValue={(val, hasError) =>
                    handleBannerTextChange(lang.code, 'title', val, hasError)
                  }
                  visible
                />
                <TextArea
                  value={data.description || ''}
                  label={pepirisDescriptionUiSchema.label}
                  uiSchema={pepirisDescriptionUiSchema}
                  updateValue={(val, hasError) =>
                    handleBannerTextChange(
                      lang.code,
                      'description',
                      val,
                      hasError
                    )
                  }
                  trim={false}
                />
              </>
            );
          }}
        />
        <PepIrisGuidelinesSection
          enabled={pepirisDisplay.targetGuidelinesEnabled}
          guidelines={pepirisDisplay.targetGuidelines || []}
          languages={languages}
          toggleSchema={targetGuidelinesToggleSchema}
          toggleId='target-guidelines'
          addTestId='add-target-guideline'
          onToggle={(enabled) =>
            handleGuidelinesToggle(
              'targetGuidelinesEnabled',
              'targetGuidelines',
              enabled
            )
          }
          onChange={(index, langCode, value, hasError) =>
            handleGuidelineChange(
              'targetGuidelines',
              index,
              langCode,
              value,
              hasError
            )
          }
          onAdd={() => handleAddGuideline('targetGuidelines')}
          onRemove={(index) => handleRemoveGuideline('targetGuidelines', index)}
        />
        <PepIrisGuidelinesSection
          enabled={pepirisDisplay.photoGuidelinesEnabled}
          guidelines={pepirisDisplay.photoGuidelines || []}
          languages={languages}
          toggleSchema={photoGuidelinesToggleSchema}
          toggleId='photo-guidelines'
          addTestId='add-photo-guideline'
          onToggle={(enabled) =>
            handleGuidelinesToggle(
              'photoGuidelinesEnabled',
              'photoGuidelines',
              enabled
            )
          }
          onChange={(index, langCode, value, hasError) =>
            handleGuidelineChange(
              'photoGuidelines',
              index,
              langCode,
              value,
              hasError
            )
          }
          onAdd={() => handleAddGuideline('photoGuidelines')}
          onRemove={(index) => handleRemoveGuideline('photoGuidelines', index)}
        />
      </PageContainer>
    </div>
  );
};

PepIrisDisplay.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      title: PropTypes.string
    })
  ).isRequired
};

export default PepIrisDisplay;
